import * as RDialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { Button } from './Button';

export const Dialog = RDialog.Root;
export const DialogTrigger = RDialog.Trigger;
export const DialogClose = RDialog.Close;

export function DialogContent({
  title,
  description,
  children,
  className,
  hideClose,
  onInteractOutside,
}: {
  title: string;
  description?: ReactNode;
  children?: ReactNode;
  className?: string;
  hideClose?: boolean;
  onInteractOutside?: (e: Event) => void;
}) {
  return (
    <RDialog.Portal>
      <RDialog.Overlay className="fixed inset-0 z-50 animate-fade-in bg-overlay backdrop-blur-[2px]" />
      <div className="pointer-events-none fixed inset-0 z-50 flex items-end justify-center p-0 sm:items-center sm:p-4">
        <RDialog.Content
          onInteractOutside={onInteractOutside}
          className={cn(
            'pointer-events-auto relative flex max-h-[90dvh] w-full max-w-lg animate-pop-in flex-col overflow-hidden rounded-t-3xl border border-line bg-surface shadow-lift focus:outline-none sm:rounded-3xl',
            className,
          )}
        >
          <div className="overflow-y-auto p-6 pb-[max(1.5rem,var(--safe-bottom))]">
            <RDialog.Title className="pr-8 font-heading text-xl font-semibold text-ink">{title}</RDialog.Title>
            {description && (
              <RDialog.Description asChild>
                <div className="mt-2 text-[0.95rem] leading-relaxed text-ink-muted">{description}</div>
              </RDialog.Description>
            )}
            {children}
          </div>
          {!hideClose && (
            <RDialog.Close
              aria-label="Close"
              className="absolute right-3 top-3 grid size-9 place-items-center rounded-full text-ink-muted transition hover:bg-hover hover:text-ink"
            >
              <X className="size-5" />
            </RDialog.Close>
          )}
        </RDialog.Content>
      </div>
    </RDialog.Portal>
  );
}

/** Small confirm dialog used for destructive actions. */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  onConfirm,
  loading,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: ReactNode;
  confirmLabel: string;
  onConfirm: () => void;
  loading?: boolean;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title={title} description={description} className="max-w-md">
        <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <DialogClose asChild>
            <Button variant="secondary">Cancel</Button>
          </DialogClose>
          <Button variant="danger" onClick={onConfirm} loading={loading}>
            {confirmLabel}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
